import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Alert from '@mui/material/Alert'
import Divider from '@mui/material/Divider'

import * as MessageList from 'components/MessageList'
import * as AccountService from 'services/accounts'
import * as AccountsTable from 'components/Admin/Accounts/AccountsTable'
import * as AccountsTableColumns from 'components/Admin/Accounts/AccountsTableColumns'

import { useBoolean } from 'utils/use-boolean'
import { CustomButton } from 'components/CustomButton'

const PendingInvites = () => {
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedAccount, setSelectedAccount] = useState(undefined)
  const [isCreateModalOpen, openCreateModal, closeCreateModal] = useBoolean(
    false
  )
  const [isDeleteModalOpen, openDeleteModal, closeDeleteModal] = useBoolean(
    false
  )
  const [messages, sendMessage] = MessageList.useMessages()
  const [
    createModalMessages,
    sendCreateModalMessage,
    clearCreateModalMessages
  ] = MessageList.useMessages()

  const refreshAccounts = () =>
    AccountService.fetchPendingInviteAccounts().then(accounts =>
      setAccounts(accounts)
    )

  // Initial load
  useEffect(() => {
    refreshAccounts().finally(() => setLoading(false))
  }, [])

  const onSelectAccount = useCallback(
    account => setSelectedAccount(account),
    []
  )

  const onConfirmCreate = useCallback(
    async emailAddress => {
      setLoading(true)
      clearCreateModalMessages()
      try {
        await AccountService.createInviteByEmail(emailAddress)
      } catch (error) {
        sendCreateModalMessage(dismiss => (
          <CreateFailureMessage
            emailAddress={emailAddress}
            dismiss={dismiss}
            errorMessage={error.message}
          />
        ))
        setLoading(false)
        return false
      }
      closeCreateModal()
      clearCreateModalMessages()
      sendMessage(dismiss => (
        <CreateSuccessMessage emailAddress={emailAddress} dismiss={dismiss} />
      ))
      // Don't need to wait for this
      refreshAccounts().then(() => setLoading(false))
      return true
    },
    [
      sendMessage,
      sendCreateModalMessage,
      clearCreateModalMessages,
      closeCreateModal
    ]
  )

  const onConfirmResend = useCallback(async () => {
    setLoading(true)
    try {
      await AccountService.resendInviteByEmail(selectedAccount.EmailAddress)
      sendMessage(dismiss => (
        <ResendSuccessMessage account={selectedAccount} dismiss={dismiss} />
      ))
    } catch (error) {
      sendMessage(dismiss => (
        <ResendFailureMessage account={selectedAccount} dismiss={dismiss} />
      ))
    } finally {
      setLoading(false)
    }
  }, [sendMessage, selectedAccount])

  const onConfirmDelete = useCallback(async () => {
    setLoading(true)
    closeDeleteModal()
    try {
      await AccountService.deleteInviteByUserId(selectedAccount.UserId)
      sendMessage(dismiss => (
        <DeleteSuccessMessage account={selectedAccount} dismiss={dismiss} />
      ))
      await refreshAccounts()
    } catch (error) {
      sendMessage(dismiss => (
        <DeleteFailureMessage
          account={selectedAccount}
          dismiss={dismiss}
          errorMessage={error.message}
        />
      ))
    } finally {
      setLoading(false)
    }
  }, [sendMessage, selectedAccount, closeDeleteModal])

  return (
    <Container maxWidth={false} sx={{ p: 2, margin: 0, width: '100%' }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: '400' }}>
        Pending invites
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <MessageList.MessageList messages={messages} />
      <AccountsTable.AccountsTable
        accounts={accounts}
        columns={[
          AccountsTableColumns.EmailAddress,
          AccountsTableColumns.DateInvited,
          AccountsTableColumns.Inviter
        ]}
        loading={loading}
        selectedAccount={selectedAccount}
        onSelectAccount={onSelectAccount}
      >
        <TableActions
          canCreate={!loading}
          onClickCreate={openCreateModal}
          canResend={!loading && selectedAccount}
          onClickResend={onConfirmResend}
          canDelete={!loading && selectedAccount}
          onClickDelete={openDeleteModal}
        />
      </AccountsTable.AccountsTable>
      <CreateInviteModal
        onConfirm={onConfirmCreate}
        open={isCreateModalOpen}
        onClose={closeCreateModal}
        messages={createModalMessages}
      />
      <DeleteInviteModal
        account={selectedAccount}
        onConfirm={onConfirmDelete}
        open={isDeleteModalOpen}
        onClose={closeDeleteModal}
      />
    </Container>
  )
}
export default PendingInvites

const TableActions = ({
  canCreate,
  onClickCreate,
  canResend,
  onClickResend,
  canDelete,
  onClickDelete
}) => (
  <ButtonGroup variant="outlined" aria-label="table actions">
    <CustomButton disabled={!canCreate} onClick={onClickCreate}>
      Create invite...
    </CustomButton>
    <CustomButton disabled={!canResend} onClick={onClickResend}>
      Resend
    </CustomButton>
    <CustomButton disabled={!canDelete} color="error" onClick={onClickDelete}>
      Delete
    </CustomButton>
  </ButtonGroup>
)

// Pulled from https://html.spec.whatwg.org/multipage/input.html#e-mail-state-(type%3Demail) and
// optimized in a few ways for size:
// - Classes of `[A-Za-z0-9]` were shortened to the equivalent `[^_\W]`.
// - Other instances of `0-9` in classes were converted to the shorthand `\d`.
// - The whole regexp was made case-insensitive to avoid the need for `A-Za-z` in classes.
// - As we're only testing, I replaced all the non-capturing groups with capturing ones.
const validEmailRegex =
  /^[\w.!#$%&'*+/=?^`{|}~-]+@[^_\W]([a-z\d-]{0,61}[^_\W])?(\.[^_\W]([a-z\d-]{0,61}[^_\W])?)*$/i

/*
 * Note: `onConfirm` should return a boolean indicating whether the creation
 * succeeded.
 */
const CreateInviteModal = ({ onConfirm, open, onClose, messages }) => {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const isEmailValid = useMemo(() => validEmailRegex.test(email), [email])
  const onClickCreate = useCallback(async () => {
    setLoading(true)
    try {
      if (await onConfirm(email)) {
        setEmail('')
      }
    } finally {
      setLoading(false)
    }
  }, [onConfirm, email])

  // If the user stops typing, but the email is invalid, show the invalid email message as a hint
  // for why they can't proceed. Don't make the timeout so short that it'd annoy a slow typer,
  // though.
  const [needsAssistance, setNeedsAssistance] = useState(false)
  const emailEverSet = useRef(false)

  useEffect(() => {
    if (email) emailEverSet.current = true
    if (isEmailValid) {
      setNeedsAssistance(false)
    } else if (!needsAssistance && emailEverSet.current) {
      const timer = setTimeout(() => { setNeedsAssistance(true) }, 3000 /* ms */)
      return () => { clearTimeout(timer) }
    }
  }, [email, needsAssistance, isEmailValid])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create invite</DialogTitle>
      <DialogContent dividers>
        <p>
          Enter an email address below and select <strong>Create</strong> to
          send an invitation to create an account.
        </p>
        <MessageList.MessageList messages={messages} />
        {needsAssistance && !isEmailValid && !loading && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Please enter a valid email address.
          </Alert>
        )}
        <TextField
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          fullWidth
          size="small"
        />
      </DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={onClose}>
          Cancel
        </Button>
        <Button
          disableElevation
          variant="contained"
          disabled={!isEmailValid}
          onClick={onClickCreate}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const DeleteInviteModal = ({ account, onConfirm, open, onClose }) =>
  account ? (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Confirm invite deletion</DialogTitle>
      <DialogContent dividers>
        <p>
          Are you sure you want to delete this account invite for <strong>{account.EmailAddress}</strong>? This action is irreversible.
        </p>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button disableElevation variant='contained' color="error" onClick={onConfirm}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  ) : null

const CreateSuccessMessage = ({ emailAddress, dismiss }) => (
  <Alert onClose={dismiss} severity="success">
    Sent account invite to <strong>{emailAddress}</strong>.
  </Alert>
)

const CreateFailureMessage = ({ emailAddress, errorMessage, dismiss }) => (
  <Alert onClose={dismiss} severity="error">
    <div>
      <p>Failed to send account invite to <strong>{emailAddress}</strong>.</p>
      {errorMessage && <p>Error message: {errorMessage}</p>}
    </div>
  </Alert>
)

const ResendSuccessMessage = ({ account, dismiss }) => (
  <Alert onClose={dismiss} severity="success">
    Resent account invite to <strong>{account.EmailAddress}</strong>.
  </Alert>
)

const ResendFailureMessage = ({ account, errorMessage, dismiss }) => (
  <Alert onClose={dismiss} severity="error">
    <div>
      <p>Failed to resend account invite to <strong>{account.EmailAddress}</strong>.</p>
      {errorMessage && <p>Error message: {errorMessage}</p>}
    </div>
  </Alert>
)

const DeleteSuccessMessage = ({ account, dismiss }) => (
  <Alert onClose={dismiss} severity="success">
    Deleted account invite for <strong>{account.EmailAddress}</strong>.
  </Alert>
)

const DeleteFailureMessage = ({ account, errorMessage, dismiss }) => (
  <Alert onClose={dismiss} severity="error">
    <div>
      <p>Failed to delete account invite for <strong>{account.EmailAddress}</strong>.</p>
      {errorMessage && <p>Error message: {errorMessage}</p>}
    </div>
  </Alert>
)
